import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, MapPin, Phone, Calendar, Package, RotateCcw, Camera, ChevronDown, ChevronUp, Navigation, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InstallationPhotos } from "@/components/installations/InstallationPhotos";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  INSTALLATION_STATUS_LABELS, 
  INSTALLATION_STATUS_BADGE_COLORS, 
  TASK_TYPE_LABELS 
} from "@/config/constants";
import { getCustomerDisplayName, formatDate } from "@/lib/formatters";
import { InstallationWithRelations, CustomerSummary, CemeterySummary, OrderSummary } from "@/types/installations";

/** MyInstallations-sivun asennus sisältää customer erikseen */
type MyInstallation = Omit<InstallationWithRelations, 'order' | 'cemetery'> & {
  order_id: string;
  order: OrderSummary & { id: string; order_type: string };
  cemetery: CemeterySummary;
  customer: CustomerSummary | null;
};

export default function MyInstallations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [expandedPhotos, setExpandedPhotos] = useState<Set<string>>(new Set());
  const [sendingDeparted, setSendingDeparted] = useState<string | null>(null);

  const togglePhotos = (id: string) => {
    setExpandedPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { data: installations, isLoading } = useQuery({
    queryKey: ["my-installations", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Fetch installations without customer data (RLS prevents direct access)
      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          status,
          task_type,
          scheduled_date,
          completed_date,
          notes,
          order_id,
          cemetery:cemeteries (name, city),
          order:orders (id, order_number, order_type)
        `)
        .eq("installer_id", user.id)
        .is("deleted_at", null)
        .order("scheduled_date", { ascending: true, nullsFirst: false });

      if (error) throw error;

      // Get unique order IDs
      const orderIds = [...new Set(data?.map(inst => inst.order?.id).filter(Boolean))] as string[];

      // Batch fetch customer info - eliminates N+1 queries
      // Käytetään suoraan supabase RPC:tä koska fetchCustomerInfoBatch olisi circular import
      const customerInfoMap: Record<string, CustomerSummary> = {};
      if (orderIds.length > 0) {
        const { data: customerData } = await supabase.rpc('get_orders_customer_info', {
          _order_ids: orderIds
        });
        customerData?.forEach((row: { order_id?: string; first_name: string | null; last_name: string | null; company_name: string | null; phone: string | null }) => {
          if (row.order_id) {
            customerInfoMap[row.order_id] = {
              first_name: row.first_name,
              last_name: row.last_name,
              company_name: row.company_name,
              phone: row.phone,
            };
          }
        });
      }

      // Merge customer data into installations
      return data?.map(inst => ({
        ...inst,
        customer: inst.order?.id ? customerInfoMap[inst.order.id] || null : null
      })) as unknown as MyInstallation[];
    },
    enabled: !!user?.id,
  });

  // Send SMS notification
  const sendSmsMutation = useMutation({
    mutationFn: async ({ orderId, notificationType }: { orderId: string; notificationType: string }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error("Ei kirjautunut sisään");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sms-notification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.session.access_token}`,
          },
          body: JSON.stringify({ orderId, notificationType }),
        }
      );

      const result = await response.json();
      if (!response.ok && !result.skipped) {
        throw new Error(result.error || "SMS-lähetys epäonnistui");
      }
      return result;
    },
    onSuccess: (result) => {
      if (result.skipped) {
        toast.info("Asiakkaalla ei ole puhelinnumeroa");
      } else {
        toast.success("SMS lähetetty asiakkaalle");
      }
    },
    onError: (error: Error) => {
      console.error("SMS error:", error);
      toast.error(`SMS-lähetys epäonnistui: ${error.message}`);
    },
  });

  // Send "installer departed" SMS
  const handleSendDepartedSms = async (installation: MyInstallation) => {
    const orderId = installation.order_id;
    if (!orderId) {
      toast.error("Tilaus-ID:tä ei löytynyt");
      return;
    }
    setSendingDeparted(installation.id);
    try {
      await sendSmsMutation.mutateAsync({ orderId, notificationType: "installer_departed" });
    } finally {
      setSendingDeparted(null);
    }
  };

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, orderId }: { id: string; status: string; orderId: string }) => {
      const updateData: Record<string, unknown> = { status };
      
      // Jos merkitään valmiiksi, lisää completed_date
      if (status === "completed") {
        updateData.completed_date = new Date().toISOString().split("T")[0];
      }

      const { error } = await supabase
        .from("installations")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      // Send SMS when completed
      if (status === "completed") {
        try {
          await sendSmsMutation.mutateAsync({ orderId, notificationType: "installation_complete" });
        } catch (e) {
          // Don't fail the status update if SMS fails
          console.error("SMS notification failed:", e);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-installations"] });
      toast.success("Asennuksen tila päivitetty");
    },
    onError: (error) => {
      console.error("Error updating installation:", error);
      toast.error("Tilan päivitys epäonnistui");
    },
  });

  // Using getCustomerDisplayName from formatters

  const incompleteTasks = installations?.filter(i => i.status !== "completed" && i.status !== "cancelled") || [];
  const completedTasks = installations?.filter(i => i.status === "completed") || [];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Omat asennukset</h1>
            <p className="text-muted-foreground mt-1">
              {incompleteTasks.length} aktiivista tehtävää
            </p>
          </div>
        </div>

        {/* Aktiiviset tehtävät */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Aktiiviset tehtävät</h2>
          {incompleteTasks.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Ei aktiivisia tehtäviä
              </CardContent>
            </Card>
          ) : (
            incompleteTasks.map((installation) => (
              <Card key={installation.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-lg">
                          #{installation.order.order_number}
                        </CardTitle>
                        <Badge className={INSTALLATION_STATUS_BADGE_COLORS[installation.status as keyof typeof INSTALLATION_STATUS_BADGE_COLORS] || "bg-gray-500"}>
                          {INSTALLATION_STATUS_LABELS[installation.status as keyof typeof INSTALLATION_STATUS_LABELS] || installation.status}
                        </Badge>
                        <Badge variant="outline">
                          {TASK_TYPE_LABELS[installation.task_type as keyof typeof TASK_TYPE_LABELS] || installation.task_type}
                        </Badge>
                        {installation.order.order_type === "pickup_work" && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <RotateCcw className="h-3 w-3" />
                            Palautus
                          </Badge>
                        )}
                        {installation.order.order_type === "field_work" && (
                          <Badge className="flex items-center gap-1 bg-purple-100 text-purple-800 border-purple-300">
                            <Wrench className="h-3 w-3" />
                            Kenttätyö
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="text-base font-medium">
                        {getCustomerDisplayName(installation.customer)}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {installation.cemetery.name}, {installation.cemetery.city}
                      </span>
                    </div>
                    {installation.customer?.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <a 
                          href={`tel:${installation.customer.phone}`}
                          className="text-primary hover:underline"
                        >
                          {installation.customer.phone}
                        </a>
                      </div>
                    )}
                    {installation.scheduled_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>
                          Suunniteltu: {formatDate(installation.scheduled_date)}
                        </span>
                      </div>
                    )}
                    {installation.notes && (
                      <div className="flex items-start gap-2">
                        <Package className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <span className="text-muted-foreground">{installation.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* SMS notification button */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendDepartedSms(installation)}
                      disabled={sendingDeparted === installation.id || sendSmsMutation.isPending}
                    >
                      {sendingDeparted === installation.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Navigation className="h-4 w-4 mr-2" />
                      )}
                      Ilmoita: Olen matkalla
                    </Button>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <span className="text-sm font-medium">Päivitä tila:</span>
                    <Select
                      value={installation.status}
                      onValueChange={(value) =>
                        updateStatusMutation.mutate({ 
                          id: installation.id, 
                          status: value,
                          orderId: installation.order_id
                        })
                      }
                      disabled={updateStatusMutation.isPending}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Odottaa</SelectItem>
                        <SelectItem value="in_progress">Käynnissä</SelectItem>
                        <SelectItem value="completed">Valmis</SelectItem>
                        <SelectItem value="cancelled">Peruttu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Collapsible open={expandedPhotos.has(installation.id)}>
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-2"
                        onClick={() => togglePhotos(installation.id)}
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Kuvadokumentointi
                        {expandedPhotos.has(installation.id) ? (
                          <ChevronUp className="h-4 w-4 ml-auto" />
                        ) : (
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-4">
                      <InstallationPhotos installationId={installation.id} />
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Valmiit tehtävät */}
        {completedTasks.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Valmiit tehtävät</h2>
            {completedTasks.map((installation) => (
              <Card key={installation.id} className="opacity-75">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-lg">
                          #{installation.order.order_number}
                        </CardTitle>
                        <Badge className={INSTALLATION_STATUS_BADGE_COLORS[installation.status as keyof typeof INSTALLATION_STATUS_BADGE_COLORS] || "bg-gray-500"}>
                          {INSTALLATION_STATUS_LABELS[installation.status as keyof typeof INSTALLATION_STATUS_LABELS] || installation.status}
                        </Badge>
                      </div>
                      <CardDescription>
                        {getCustomerDisplayName(installation.customer)}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {installation.cemetery.name}, {installation.cemetery.city}
                      </span>
                    </div>
                    {installation.completed_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>
                          Valmistunut: {formatDate(installation.completed_date)}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
